import axios from 'axios';
import { Helpers } from '../helpers/Helpers';
import { ColumnChooserModel } from '../models/common/ColumnChooserModel';
import { ColumnFilterModel } from '../models/common/ColumnFilterModel';

/**
 * Kargo Modülü > Yurtiçi Kargo > Gider Mutabakatı (24.10.2026, #485): Yurtiçi faturasındaki gönderi tutarı ile siparişin
 * beklenen gideri (KDV hariç) gönderi kodu bazında karşılaştırılır. Ekran yalnız gösterir ve "İncelendi" işareti koydurur.
 */

export const MUTABAKAT_SAYFASI = 'Kargo Modülü > Yurtiçi Kargo > Gider Mutabakatı';
/** Zamanlama ve bildirim alıcıları (24.10.2026). */
export const MUTABAKAT_AYAR_SAYFASI = 'Ayarlar > Yurtiçi Gider Mutabakatı';

export const DURUM = { ESLESTI: 1, TUTAR_FARKLI: 2, KEYLOGXTE_YOK: 3, BEKLENEN_GIDER_YOK: 4, RAPORDA_YOK: 5, AYRI_SINIF: 6 };

/** Sekme sırası: önce farklar ve eksikler, en sonda eşleşenler. */
export const DURUM_SEKMELERI: { value: number | undefined, label: string, renk: string }[] = [
    { value: DURUM.TUTAR_FARKLI, label: 'Tutar farklı', renk: 'danger' },
    { value: DURUM.KEYLOGXTE_YOK, label: "Keylogx'te yok", renk: 'warning' },
    { value: DURUM.BEKLENEN_GIDER_YOK, label: 'Beklenen gider yok', renk: 'warning' },
    { value: DURUM.RAPORDA_YOK, label: 'Raporda yok', renk: 'info' },
    { value: DURUM.AYRI_SINIF, label: 'Ayrı sınıf (iade)', renk: 'secondary' },
    { value: DURUM.ESLESTI, label: 'Eşleşti', renk: 'success' },
    { value: undefined, label: 'Tümü', renk: 'primary' },
];

export const durumRengi = (durum: number) => DURUM_SEKMELERI.find(x => x.value == durum)?.renk ?? 'secondary';

export interface MutabakatListIstegi {
    searchTerm: string
    skip: number
    take: number
    currentPage: number
    columnChooser: ColumnChooserModel[] | undefined
    sortColumn?: string
    sortDirection?: string
    columnFilters: ColumnFilterModel[]
    ids?: string[]
    durum?: number
    baslangicTarihi?: string
    bitisTarihi?: string
    faturaNo?: string
    musteriId?: string
    /** undefined tümü, false incelenmeyenler (açılış), true incelenenler */
    incelendi?: boolean
}

const gun = (d: Date) => d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);

export const ilkMutabakatListIstegi = (): MutabakatListIstegi => {
    const bit = new Date();
    const bas = new Date();
    bas.setDate(bit.getDate() - 30);

    return {
        searchTerm: '',
        skip: 0,
        take: 50,
        currentPage: 1,
        columnChooser: undefined,
        columnFilters: [],
        durum: DURUM.TUTAR_FARKLI,
        baslangicTarihi: gun(bas),
        bitisTarihi: gun(bit),
        incelendi: false
    }
}

export interface MutabakatSatiri {
    yurticiMutabakatSatiriId: string
    gonderiKodu: string
    yurticiCargoId?: string
    siparisRefNo?: string
    musteriAdi?: string
    gonderiTarihiStr?: string
    faturaNo?: string
    faturaTarihiStr?: string
    faturaTipi?: string
    raporTutari?: number
    keylogxTutari?: number
    webServisTutari?: number
    fark?: number
    durum: number
    durumAdi: string
    kaynakAdi?: string
    incelendi: boolean
    incelendiAdi: string
    incelemeNotu?: string
    isSelected?: boolean
}

export interface MutabakatSayilari {
    durumlar: { [durum: number]: number }
    toplam: number
    kayitSayisi: number
    artiFark: number
    eksiFark: number
}

export interface MutabakatDetayi {
    satir: MutabakatSatiri
    urunBedeli?: number
    matrah?: number
    kdv?: number
    kdvDahil?: number
    desiKg?: number
    kargoAdedi?: number
    kargoStatusu?: string
    musteriKodu?: string
    webServisZamani?: string
    karsilastirmaZamani?: string
    incelemeYapan?: string
    incelemeZamani?: string
    giderSatirlari: { hizmetAdi?: string, kdvHaric?: number, kdv?: number, kdvDahil?: number, faturaNo?: string, aciklama?: string }[]
    gecmis: { zaman: string, kaynakAdi?: string, tutar?: number, faturaNo?: string, aciklama: string, kullanici?: string }[]
}

export interface MutabakatCalismasi {
    yurticiMutabakatCalismasiId: string
    baslamaZamaniStr: string
    bitisZamaniStr?: string
    turAdi: string
    tetikleyenAdi: string
    sonucAdi: string
    okunanMail: number
    indirilenRapor: number
    aktarilanSatir: number
    dosyaAdlari?: string
    eslesen: number
    tutarFarkli: number
    keylogxteYok: number
    beklenenGiderYok: number
    rapordaYok: number
    ayriSinif: number
    mesaj?: string
}

export interface MutabakatOzeti {
    tutarFarkli: number
    tutarFarkliToplam: number
    artiFark: number
    eksiFark: number
    keylogxteYok: number
    rapordaYok: number
    beklenenGiderYok: number
    karsilastirilan: number
    eslesen: number
    mutabakatOrani?: number
    sonBasariliRapor?: string
    sonRaporGun?: number
}

export interface MutabakatAyari {
    aktif: boolean
    /** 1 pazartesi ... 7 pazar */
    gunler: number[]
    /** "HH:mm" */
    saatler: string[]
    kime?: string
    bilgi?: string
    ozet: string
    sonCalisma?: string
    sonrakiCalisma?: string
    /** Hatadan sonraki kendiliğinden tekrar (0 = yok); sonrakiCalisma o zaman tekrar zamanı */
    tekrarDenemesi: number
    tekrarSiniri: number
    sonrakiler: string[]
    mailKutusuHazir: boolean
    mailKutusu?: string
    zamanlayiciAcik: boolean
}

export interface MutabakatAyarKaydet {
    aktif: boolean
    gunler: number[]
    saatler: string[]
    kime?: string
    bilgi?: string
}

/** "5,40 ₺"; boşsa "—". */
export const tl = (n: number | null | undefined) => n == null ? '—' : n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₺';

const cagir = async (yol: string, inputObj: any) => {
    try {
        console.log('SERVICE CALL - YurticiMutabakat/' + yol, JSON.stringify(inputObj));

        const resp = await axios.post(Helpers.getEndpoint() + '/YurticiMutabakat/' + yol, inputObj);

        return Helpers.dataParser(resp);
    } catch (error) {
        throw Helpers.parseErrorMessage(error);
    }
}

export class YurticiMutabakatService {
    static getList(inputObj: MutabakatListIstegi) { return cagir('GetListPagingAndFilter', inputObj); }
    static exportList(inputObj: MutabakatListIstegi) { return cagir('ExportList', inputObj); }
    static sayilar(inputObj: MutabakatListIstegi) { return cagir('Sayilar', inputObj); }
    static detay(id: string) { return cagir('Detay', { id }); }
    static incele(ids: string[], not: string) { return cagir('Incele', { ids, not }); }
    static calismaListesi(inputObj: any) { return cagir('CalismaListesi', inputObj); }
    static calismaExport(inputObj: any) { return cagir('CalismaExport', inputObj); }
    static simdiCalistir() { return cagir('SimdiCalistir', {}); }
    static ozet() { return cagir('Ozet', {}); }
    static ayarGetir() { return cagir('AyarGetir', {}); }
    static ayarKaydet(inputObj: MutabakatAyarKaydet) { return cagir('AyarKaydet', inputObj); }
    static zamanOnizle(inputObj: MutabakatAyarKaydet) { return cagir('ZamanOnizle', inputObj); }
}
